<template>
<div class="chapter">
<div class="inner_page">
<h3 class="calibre14" id="PART_I_CHAPTER_III">CHAPTER III.<br class="calibre9"/>
<span class="smaller">THE PENETRATION OF THE REGIONS WITHIN THE POLAR
CIRCLE; THE PERIOD OF THE NORTH-WEST AND
NORTH-EAST PASSAGES.</span></h3>
<p class="calibre3">1. Around the lonely apex of the Pole stand cairns of
stone which serve to mark the points to which the restless
spirit of human enterprise and discovery has penetrated. In
its zenith wheels the sea-gull in its flight, and the harpoon-persecuted
seal finds on its ice-floes an unapproachable
asylum; but the Pole itself remains the goal which no
human effort has yet reached.</p>
<p class="calibre3">2. As all knowledge is perfected slowly and gradually, so
man’s knowledge of the earth and its configuration forms no
exception to this general rule. Of the few attempts of early
antiquity to enlarge the domain of geographical knowledge,
tradition tells us only of the Argonautic expedition of the
Greeks, of the voyage of the Phœnicians to Ophir, and their
bolder circumnavigation of Africa. With the conception of
the spherical form of the earth the still vague notion of
climatal zones makes its appearance, and to this, four centuries
before Christ, Pytheas of Marseilles gave the first
scientific elucidation and the first approximation to modern
theories by his doctrine of the Polar Circle. Almost contemporaneously
Alexander’s expedition to the wonder-land of
India created a paradise for commerce and navigation, to
secure which a shortened route, <i class="calibre10">the route through the ice</i>—the
most perverse notion that ever entered into the mind of man
to conceive—was one thousand eight hundred years afterwards
eagerly and passionately sought.</p>
<p class="calibre3">3. Rome had extended her knowledge to Scandinavia, and
Seneca’s prophetical mind foresaw the discovery of new<span id="pgepubid00049" title="[21]"><a class="reference" id="Page_21" title="[21]"></a></span>
worlds. But the deluge of religious strifes, the migrations of
nations in the earlier part of the Middle Ages, the holy zeal
for destruction in the apostles to the heathen, proved formidable
barriers to the extension of geographical knowledge,
which were broken through only by the piratical hordes of
Normans so renowned in story. While the Romans boasted
that Britain had never been circumnavigated, the Normans,
throwing the deeds of the Phœnicians into the shade, discovered
Greenland, and became <i class="calibre10">the first Polar Navigators</i>.</p>
<p class="calibre3">4. Travels by land were the principal means by which the
geographical knowledge of the world was enriched; but
during the Middle Ages the information which travellers
communicated, uncertain and superficial even for Europe,
served only to supply food for the fancies of map-makers, as
far as the distant parts of the world were concerned.</p>
<p class="calibre3">5. But the grand moment at length arrived in the history of
mankind when the civilization of the West, looking beyond
the narrow horizon of the Old World, and awaking from the
geographical dreams of centuries, burst the fetters of tradition,
and within three hundred years perfected the knowledge of
our planet up to the Pole.</p>
<p class="calibre3">6. When by his famous line of partition, Pope Alexander
VI. granted to Spain and Portugal the new countries discovered
in the East and West, the brigantines of these nations
spread themselves over all seas in search of new lands and
fresh glory. To the other maritime nations, to the English
and the Dutch, nothing remained, if they meant to acquire
gold-yielding lands, but to drive the Spaniards and Portuguese
from their conquests, or to seek new Eldorados—yea, by the
discovery of sea routes on the north of Asia and America, to
aspire to India itself. This was the conception first entertained
by both the English and the Dutch, and Geography at
any rate profited by their delusions. These nations were
not to blame if those routes, known afterwards as the <i class="calibre10">North-West</i>
and <i class="calibre10">North-East passages</i>, degenerated into chimeras, if
passages had to be sought in higher and still higher latitudes,—ultimately
in the ice itself, although the Dutch geographer,
Plancius, struck out the consoling theory of the <i class="calibre10">open Polar
Sea</i>.</p>
<p class="calibre3">7. But who in those days could presuppose that the<span id="pgepubid00050" title="[22]"><a class="reference" id="Page_22" title="[22]"></a></span>
continents of Asia and America, just where those passages
were attempted, symmetrically developed the most enormous
longitudinal dimensions? Even the actual discovery of the
vast extent of Siberia exerted but little influence on the
question of the North-East passage, for the achievements of
individuals were not then so quickly disseminated as at
present. A succession of men in vessels poorly equipped
now struggled against the supremacy of the ice, avoiding at
first the dreaded wintering, while they attempted sometimes
the North-East, sometimes the North-West, sometimes the
passage over the Pole itself. In these attempts many lost
their lives; many returned, despairing of but still hoping
for the solution of the problems—<i class="calibre10">but no one reached the goal</i>.</p>
<p class="calibre3">8. The amazing simplicity of the first adventurers is seen
in Frobisher’s project to erect forts, duly provided with cannons
and men, on the commanding points of the passage, in
the letters of recommendation given by kings of England to
the leaders of the expedition for the small Saracenic states
which were supposed to exist beyond the river Obi; but
these old navigators carried no letter of recommendation to
the great potentate—the ice. Gold, too, they hoped to find
in the North, because the book of Job speaks of gold coming
from thence, and the North-East passage was considered as
free from danger, because Pliny mentions some Indians who
had been driven towards Norway!</p>
<p class="calibre3">9. When another century and a half had elapsed, a series
of unsuccessful attempts to force the North-East passage put
a decisive check to material interests in Polar expeditions.
The North-East passage belonged henceforward to the history
of the past. The English and Dutch withdrew from the
Novaya Zemlya seas; and after Wood’s retreat no scientific
expedition entered those seas for two hundred years, <i class="calibre10">until the
days of the Austrian Expeditions</i>.</p>
<p class="calibre3">10. Among the maritime nations of Europe, it was England,
and especially her merchants, who had hitherto largely invested
in the costs and risks of these Argonautic expeditions
“for the glory of God and the good of the country.” The
Dutch soon contented themselves, after Barentz’s death, with
the capture of whales in the Arctic seas; France remained
an unconcerned spectator, while the sylphs of Versailles<span id="pgepubid00051" title="[23]"><a class="reference" id="Page_23" title="[23]"></a></span>
consumed the whalebone of whole fleets of whalers; and Spain
and Portugal early withdrew from seas in which, instead of
ingots of gold, ice-floes only were to be found. But even for
England the days of the prophets had now passed away—the
days of a Cabot, a Mercator,<a class="reference" id="FNanchor_10"></a><span class="fnanchor" href="#/Text/Section0043#Footnote_10" title="Link target outside of Sample">[10]</span> a Wolstenholme, and a
Walsingham. Men of weight raised their voices against the
chimeras of Arctic commercial routes, and Chillingworth contemptuously
compared an expedition for the discovery of the
North-East passage to the study of the Fathers.</p>
<p class="calibre3">11. It may be asked why nations struggled with dauntless
ambition for the lost cause of the barren North-West
and North-East passages, while for a century they stretched
forth timid hands after the rich treasures of lands lying in
the more favoured zones? <i class="calibre10">The mighty stimulus of the love of
the marvellous</i> explains this series of efforts taken up by
generation after generation. Frobisher, Davis, Baffin, and
the Novaya Zemlya adventurers, told on their return of gold-lands
far within the domains of the icy Hydra. Their tales of
single combats with spear or matchlock against polar bears, of
the dreadful snow-storms and fearful cold of the Arctic winter,
were heard with grim delight by listeners on whom no hardships
were imposed. Or they spoke of a darkness that continued
for months, of the flaming arches of the northern
lights, of the sun remaining visible for many weeks in the
heavens, of a race of dwarfs, of unheard-of animals, of fish
as big as ships of war, of monsters with long teeth which
precisely resembled the Sphinxes of the plains of the Pyramids,
of white and blue foxes, of floating mountains of
dazzling crystal, of ships seen upside down in the air—when
had ever the mind of man more food to nourish the love of
the marvellous or greater incentives to stimulate the love of
distinction? But besides these appeals to the imagination,
every generation desires new confirmations of its convictions;
and hence geographical questions, after being shelved for a
time, come again to the front as by an inward necessity.</p>
<p class="calibre3">12. If the earlier Polar expeditions pursued exclusively
material ends, a decided change appears in those of the
present century—the Polar world itself became an object of
scientific investigation. With Sir John Ross (1818) began a<span id="pgepubid00052" title="[24]"><a class="reference" id="Page_24" title="[24]"></a></span>
series of expeditions, at first subservient to the idea of a
North-West passage, but which ultimately derived all their
importance from their attempt—ineffectual as it proved—to
rescue the lives of 139 men, who had fallen far from the fields
and scenes where earthly fame is commonly achieved. It
was these expeditions, still fresh in the memory of this generation,
which, summoning to their aid the modern power of
steam against the ice, succeeded in drawing on our Arctic
maps a circle whose mean distance was 200 (German) miles
from the Pole. Parry on the frozen sea of Spitzbergen had
approached it within 100 miles (German); Kane, Hayes and
Hall on the coast of the Kennedy Channel, the former to
within 116, and the two latter to within 108 miles, and the
Austro-Hungarian expedition to within 109 miles.</p>
<p class="calibre3">13. M’Clintock, who returned with the relics of the
Franklin expedition, succeeded in perfecting a mode of
discovery independent of the ship—that by means of sledging—admirably
adapted for future Arctic expeditions. But
the North-West passage for which six generations had toiled,
though discovered, was shown to be utterly worthless for all
material purposes—a dreary web of coast lines.</p>
<p class="calibre3"><span id="pgepubid00053" title="[25]"><a class="reference" id="Page_25" title="[25]"></a></span></p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Section0009.vue?18f68254